import { FC, ReactNode, memo, useMemo } from 'react';
import { Asterisk } from 'lucide-react';

import { PunchupEntityTile } from 'features/common/components/Punchup/primitives/display/PunchupTile';
import { PunchupTicketTypeTag } from 'features/common/components/Punchup/primitives/display/PunchupTicketTypeTag';
import styles from './PunchupCustomQuestionTile.module.css';

export interface PunchupCustomQuestionTileProps {
	/** The question text — the tile's primary text (16px / 600). */
	question: string;
	/** Small top-left label: who answers this question (e.g. "Only buyer" / "Each attendee"). */
	answererLabel: string;
	/** Required vs optional — shown as a top-right asterisk glyph rather than a text row. */
	required: boolean;
	/** Bound ticket-type names — a label-less colour-tinted chip row (or one "All ticket types" chip). */
	ticketTypeNames: string[];
	/**
	 * Member ticket-type colours, parallel to `ticketTypeNames` — chip `i` is tinted with `ticketTypeColors[i]`.
	 * Resolve from `useTicketTypeColors` at the (data-aware) callsite; omit for neutral grey chips. A composite
	 * stays data-free, so the colours are passed in, not fetched.
	 */
	ticketTypeColors?: (string | null)[];
	/** Bottom-right help tooltip — pass a span-based `PunchupHelpButton` (safe inside a clickable tile). */
	helpButton?: ReactNode;
	/** When set the whole tile is clickable (opens the inline edit form). */
	onClick?: () => void;
	dataCy?: string;
}

/**
 * The Custom Questions dashboard tile, modelled on `PunchupPoolTile`. Wraps `PunchupEntityTile`: the
 * answerer label is the small top-left title, the question is the primary text, required-ness shows as a
 * top-right asterisk (not a text row), the bound ticket types render as label-less colour-tinted
 * `PunchupTicketTypeTag` chips (the SAME chips the tables and pool tiles use), and an optional help tooltip
 * sits bottom-right. The chips are built here (a composite may compose primitives) and handed to the tile
 * via `tagSlot`, so the `PunchupEntityTile` primitive stays chip-agnostic.
 */
export const PunchupCustomQuestionTile: FC<PunchupCustomQuestionTileProps> = memo(
	({ question, answererLabel, required, ticketTypeNames, ticketTypeColors, helpButton, onClick, dataCy }) => {
		// Colour-tinted chips — same PunchupTicketTypeTag the tables use. Memoised so the memoised tile
		// isn't re-rendered by a fresh node each pass.
		const typeChips = useMemo(
			() =>
				ticketTypeNames.map((name, i) => <PunchupTicketTypeTag key={name} name={name} color={ticketTypeColors?.[i]} />),
			[ticketTypeNames, ticketTypeColors]
		);

		return (
			<PunchupEntityTile
				title={answererLabel}
				name={question}
				tagSlot={ticketTypeNames.length > 0 ? typeChips : undefined}
				topRight={required ? <Asterisk size={16} className={styles.requiredIcon} aria-label="Required" /> : undefined}
				helpButton={helpButton}
				onClick={onClick}
				dataCy={dataCy}
			/>
		);
	}
);

PunchupCustomQuestionTile.displayName = 'PunchupCustomQuestionTile';
